// src/api/googleSheetsExport.js
// Google Sheets Export Service for OFF/Astreintes data

import { getValidAccessToken, isGoogleAuthenticated } from './googleDrive';

const OFFDAYS_SHEET_ID = process.env.REACT_APP_GOOGLE_OFFDAYS_SHEET_ID;

/**
 * Make authenticated request to Google Sheets API
 */
const sheetsApiRequest = async (url, options = {}) => {
  const accessToken = await getValidAccessToken();

  const response = await fetch(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  return response;
};

/**
 * Clear a sheet and write new data
 */
export const clearAndWriteSheet = async (spreadsheetId, range, values) => {
  try {
    // First, clear the range
    await sheetsApiRequest(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}:clear`,
      { method: 'POST' }
    );

    // Then write new data
    const response = await sheetsApiRequest(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`,
      {
        method: 'PUT',
        body: JSON.stringify({ values }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to write to sheet');
    }

    return await response.json();
  } catch (error) {
    console.error('Error writing to sheet:', error);
    throw error;
  }
};

/**
 * Read data from a sheet
 */
export const readSheetData = async (spreadsheetId, range) => {
  try {
    const response = await sheetsApiRequest(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to read sheet data');
    }

    const data = await response.json();
    return data.values || [];
  } catch (error) {
    console.error('Error reading sheet data:', error);
    throw error;
  }
};

/**
 * Get sheet info
 */
export const getSheetInfo = async (spreadsheetId) => {
  try {
    const response = await sheetsApiRequest(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to get sheet info');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting sheet info:', error);
    throw error;
  }
};

/**
 * Format OFF/Astreintes data for export
 */
export const formatOffDaysForExport = (offDates, employees) => {
  // Create employee lookup
  const employeeMap = {};
  employees.forEach(emp => {
    const firstName = emp.first_name || '';
    const lastName = emp.name || '';
    employeeMap[emp.id] = `${firstName} ${lastName}`.trim() || 'Unknown';
  });

  // Header row
  const headers = ['Date', 'Employé', 'Catégorie', 'Rôle'];

  // Sort by date descending
  const sortedData = [...offDates].sort((a, b) =>
    new Date(b.off_date) - new Date(a.off_date)
  );

  // Data rows
  const rows = sortedData.map(entry => {
    const employee = employees.find(e => e.id === entry.employee_id);
    return [
      entry.off_date,
      employeeMap[entry.employee_id] || 'Unknown',
      entry.category,
      employee?.role || 'N/A'
    ];
  });

  return [headers, ...rows];
};

/**
 * Export OFF/Astreintes data to Google Sheet
 */
export const exportOffDaysToSheet = async (offDates, employees, sheetId = null) => {
  const spreadsheetId = sheetId || OFFDAYS_SHEET_ID;

  if (!spreadsheetId) {
    throw new Error('Google Sheet ID not configured. Please set REACT_APP_GOOGLE_OFFDAYS_SHEET_ID in your .env file');
  }

  if (!isGoogleAuthenticated()) {
    throw new Error('Not authenticated with Google. Please authenticate first.');
  }

  console.log('=== EXPORTING OFF/ASTREINTES TO GOOGLE SHEETS ===');
  console.log('Sheet ID:', spreadsheetId);
  console.log('Total entries:', offDates.length);
  console.log('Total employees:', employees.length);

  try {
    // Format data
    const formattedData = formatOffDaysForExport(offDates, employees);
    console.log('Formatted rows:', formattedData.length);

    // Write to sheet (Sheet1!A1 is the default range)
    const result = await clearAndWriteSheet(spreadsheetId, 'Sheet1!A1', formattedData);
    console.log('Data written to sheet:', result);

    // Get sheet URL
    const sheetInfo = await getSheetInfo(spreadsheetId);
    const sheetUrl = sheetInfo.spreadsheetUrl;

    console.log('=== EXPORT COMPLETE ===');
    console.log('Sheet URL:', sheetUrl);

    return {
      success: true,
      rowsWritten: formattedData.length,
      sheetUrl,
      spreadsheetId
    };
  } catch (error) {
    console.error('Error exporting to Google Sheets:', error);
    throw error;
  }
};

/**
 * Create a new Google Sheet for OFF/Astreintes
 */
export const createOffDaysSheet = async (title = 'OFF & Astreintes - Masterski') => {
  try {
    const accessToken = await getValidAccessToken();

    const response = await fetch('https://sheets.googleapis.com/v4/spreadsheets', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        properties: {
          title,
        },
        sheets: [{
          properties: {
            title: 'Sheet1',
            gridProperties: {
              frozenRowCount: 1 // Freeze header row
            }
          }
        }]
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to create spreadsheet');
    }

    const spreadsheet = await response.json();
    console.log('Created new spreadsheet:', spreadsheet.spreadsheetId);
    console.log('URL:', spreadsheet.spreadsheetUrl);

    return {
      spreadsheetId: spreadsheet.spreadsheetId,
      spreadsheetUrl: spreadsheet.spreadsheetUrl,
      title: spreadsheet.properties.title
    };
  } catch (error) {
    console.error('Error creating spreadsheet:', error);
    throw error;
  }
};

/**
 * Update a specific cell in a Google Sheet
 */
export const updateSheetCell = async (spreadsheetId, range, value) => {
  try {
    const response = await sheetsApiRequest(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`,
      {
        method: 'PUT',
        body: JSON.stringify({ values: [[value]] }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to update cell');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating cell:', error);
    throw error;
  }
};

/**
 * Update a row in a Google Sheet
 */
export const updateSheetRow = async (spreadsheetId, range, values) => {
  try {
    const response = await sheetsApiRequest(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`,
      {
        method: 'PUT',
        body: JSON.stringify({ values: [values] }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to update row');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating row:', error);
    throw error;
  }
};

/**
 * Clear a row in a Google Sheet (delete by clearing content)
 */
export const clearSheetRow = async (spreadsheetId, range) => {
  try {
    const response = await sheetsApiRequest(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}:clear`,
      { method: 'POST' }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to clear row');
    }

    return await response.json();
  } catch (error) {
    console.error('Error clearing row:', error);
    throw error;
  }
};

/**
 * Append a row to a Google Sheet
 */
export const appendSheetRow = async (spreadsheetId, range, values) => {
  try {
    const response = await sheetsApiRequest(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`,
      {
        method: 'POST',
        body: JSON.stringify({ values: [values] }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to append row');
    }

    return await response.json();
  } catch (error) {
    console.error('Error appending row:', error);
    throw error;
  }
};

export default {
  exportOffDaysToSheet,
  createOffDaysSheet,
  formatOffDaysForExport,
  clearAndWriteSheet,
  getSheetInfo,
  readSheetData,
  updateSheetCell,
  updateSheetRow,
  clearSheetRow,
  appendSheetRow
};
